import type { CourseStatusMap } from '@/services/progressApi';
import { getCtaLabel, getCtaUrl } from '@/composables/useCourseActions';
import { courseDetailPath, kindForTypeCode } from '@/utils/credentialTypes';

/**
 * The Continue Learning tile's action label and destination (BPE-739).
 *
 * Extracted from `DashboardView` for the same reason `browseFilterParams` was
 * extracted from `BrowseView`: the interesting parts are guards that are
 * invisible on the happy path, and the view is far too heavy to mount for them.
 *
 * The tile keeps its own shorter vocabulary — "Continue" rather than the rows'
 * "Continue Course" — and its own pre-load fallback, so it cannot simply call
 * the row helpers and be done. What it must not have is its own idea of which
 * variant a card represents, or of when a simulation reads differently.
 */

/**
 * The variant kind a tile hands the CTA helpers, and only when it changes the
 * wording.
 *
 * `learningObjectTypeCode` is already "which non-credit kind is this card
 * wearing": `courseTypeTags` sets it from the course's first kind precisely
 * when no credential chip won, so a dual CLE+Simulation course leaves it empty
 * and the tile keeps credential wording. That matches the rule the row surfaces
 * follow — the wording belongs to whichever variant the card represents.
 *
 * Narrowed to Simulation on purpose. Every other tile keeps the bare kindless
 * link and the generic vocabulary it has today, so this cannot re-target a link
 * the ticket never asked about.
 */
export function tileVariantKind(typeCode: string | null | undefined): string | undefined {
  return kindForTypeCode(typeCode) === 'Simulation' ? 'Simulation' : undefined;
}

/**
 * Until the status map arrives, fall back to "Continue" rather than inviting a
 * claim — a missing claim button for one render is recoverable; an invalid claim
 * invitation is the defect (BPE-571).
 *
 * Learner-course ids arrive as strings from the API; the status map is keyed by
 * number.
 */
export function tileCtaLabel(
  rawId: string | number,
  statusMap: CourseStatusMap,
  progressPercent: number,
  typeCode?: string | null,
): string {
  const learningObjectId = Number(rawId);
  const variantKind = tileVariantKind(typeCode);

  if (!statusMap[learningObjectId]) {
    // The simulation vocabulary for the pre-load render too. A simulation has no
    // claim to get wrong, so the caution above doesn't apply; what would be wrong
    // is "Continue" flipping to "Resume Simulation" a moment later, or "Launch"
    // on a tile that only lists things already started.
    if (variantKind) return progressPercent === 100 ? 'Start Over' : 'Resume Simulation';
    return progressPercent === 100 ? 'View course' : 'Continue';
  }

  return getCtaLabel(learningObjectId, statusMap, variantKind);
}

/** The tile's destination, lensed to the same kind that chose its wording. */
export function tileCtaUrl(
  rawId: string | number,
  statusMap: CourseStatusMap,
  deliveryMode: string | undefined,
  typeCode?: string | null,
): string {
  const learningObjectId = Number(rawId);
  const variantKind = tileVariantKind(typeCode);
  const liveQuery = deliveryMode === 'Live' ? '?deliveryMode=Live' : '';

  if (!statusMap[learningObjectId]) {
    return `${courseDetailPath(learningObjectId, variantKind)}${liveQuery}`;
  }

  // Only the course-detail destination takes the delivery-mode param; certificate
  // and claim URLs carry their own query strings. Matched to
  // HistoryView.ctaRouteFor rather than comparing against the bare path, which
  // stopped being the only course-detail shape once the URL could be lensed to a
  // variant — the old equality check would have silently dropped the param.
  const baseUrl = getCtaUrl(learningObjectId, statusMap, variantKind);
  return baseUrl.startsWith('/course/') && !baseUrl.endsWith('/claim')
    ? `${baseUrl}${liveQuery}`
    : baseUrl;
}
